import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "./ui/button";
import { LoadingState } from "./ui/loading-state";
import { Slider } from "./ui/slider";
import { Play, Pause, Share2, Heart, Eye } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { podcastService } from "@/lib/podcastService";

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const SharedPlayer = () => {
  const { url } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const listenTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [podcastInfo, setPodcastInfo] = useState({
    title: "",
    episodeTitle: "",
    imageUrl: "",
    views: 0,
    likes: 0,
    episodeId: "",
    podcastId: "",
  });
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Decode the URL properly
  const decodedUrl = url
    ? decodeURIComponent(atob(decodeURIComponent(url)))
    : "";

  // Extract episode ID from URL
  const getEpisodeId = (url: string) => {
    try {
      const matches = url.match(/\/play\/(\d+)/);
      return matches ? matches[1] : null;
    } catch {
      return null;
    }
  };

  // Fetch episode data
  useEffect(() => {
    const fetchEpisodeData = async () => {
      if (!decodedUrl) return;

      try {
        console.log("Decoded URL:", decodedUrl);
        const episodeId = getEpisodeId(decodedUrl);
        console.log("Looking for episode ID:", episodeId);

        if (!episodeId) {
          throw new Error("Could not extract episode ID from URL");
        }

        // First, find the episode by ID
        const { data: episodes, error: episodeError } = await supabase
          .from("episodes")
          .select(
            `
            id,
            title,
            image_url,
            audio_url,
            podcast:podcast_id (
              id,
              title,
              image_url
            )
          `,
          )
          .filter("audio_url", "ilike", `%/play/${episodeId}%`);

        if (episodeError) throw episodeError;

        console.log("Found episodes:", episodes?.length);

        if (!episodes || episodes.length === 0) {
          throw new Error("Episode not found");
        }

        const episode = episodes[0]; // Take the first match

        // Get metrics for this episode
        const { data: metrics } = await supabase
          .from("episode_metrics")
          .select("listen_count, like_count")
          .eq("episode_id", episode.id)
          .single();

        console.log("Setting podcast info:", {
          episodeId: episode.id,
          podcastId: episode.podcast?.id,
          title: episode.title,
        });

        setPodcastInfo({
          title: episode.podcast?.title || "Unknown Podcast",
          episodeTitle: episode.title,
          imageUrl: episode.image_url || episode.podcast?.image_url || "",
          views: metrics?.listen_count || 0,
          likes: metrics?.like_count || 0,
          episodeId: episode.id,
          podcastId: episode.podcast?.id || "",
        });

        // If audio is already playing, start the listen timer
        if (isPlaying) {
          console.log(
            "Episode data loaded while playing, starting listen timer",
          );
          startListenTimer(); // Start the listen timer when autoplay starts
        }
      } catch (error) {
        console.error("Error fetching episode data:", error);
        // Don't set error state here as we still want to play the audio
      }
    };

    fetchEpisodeData();

    // Set up periodic refetch of metrics
    const intervalId = setInterval(async () => {
      if (podcastInfo.episodeId) {
        try {
          const { data: metrics } = await supabase
            .from("episode_metrics")
            .select("listen_count, like_count")
            .eq("episode_id", podcastInfo.episodeId)
            .single();

          if (metrics) {
            setPodcastInfo((prev) => ({
              ...prev,
              views: metrics.listen_count || prev.views,
              likes: metrics.like_count || prev.likes,
            }));
          }
        } catch (error) {
          console.error("Error refreshing metrics:", error);
        }
      }
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(intervalId);
  }, [decodedUrl]);

  useEffect(() => {
    // Update meta tags
    const updateMetaTags = () => {
      document.title = podcastInfo.title || "Listen on Krak";

      const metaTags = {
        "og:title": podcastInfo.title || "Listen on Krak",
        "og:description":
          podcastInfo.episodeTitle || "Listen to short-form audio clips",
        "og:image": podcastInfo.imageUrl,
        "og:type": "music.song",
        "og:audio": decodedUrl,
        "og:audio:type": "audio/mpeg",
        "og:url": window.location.href,
        "twitter:card": "player",
        "twitter:title": podcastInfo.title || "Listen on Krak",
        "twitter:description":
          podcastInfo.episodeTitle || "Listen to short-form audio clips",
        "twitter:image": podcastInfo.imageUrl,
        "twitter:player": window.location.href,
        "twitter:player:width": "400",
        "twitter:player:height": "400",
      };

      Object.entries(metaTags).forEach(([key, value]) => {
        let meta =
          document.querySelector(`meta[property="${key}"]`) ||
          document.querySelector(`meta[name="${key}"]`);

        if (!meta) {
          meta = document.createElement("meta");
          if (key.startsWith("og:")) {
            meta.setAttribute("property", key);
          } else {
            meta.setAttribute("name", key);
          }
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", value);
      });
    };

    updateMetaTags();
  }, [decodedUrl, podcastInfo]);

  const handleDurationChange = () => {
    if (!audioRef.current) return;
    console.log("Duration changed, starting autoplay", {
      episodeId: podcastInfo.episodeId,
      title: podcastInfo.episodeTitle,
      duration: audioRef.current.duration,
    });
    setDuration(audioRef.current.duration);
    setLoading(false);
    setIsPlaying(true); // Auto-start playback when duration is available
    startListenTimer(); // Start the listen timer when autoplay starts
  };

  const startListenTimer = () => {
    // Clear any existing timer
    if (listenTimeoutRef.current) {
      console.log("Clearing existing listen timer");
      clearTimeout(listenTimeoutRef.current);
      listenTimeoutRef.current = undefined;
    }

    // Only start timer if we have episode info
    if (!podcastInfo.episodeId) {
      console.log("No episode ID available, skipping listen timer", {
        podcastInfo: JSON.stringify(podcastInfo),
      });
      return;
    }

    console.log("Starting 10s timer for episode:", {
      title: podcastInfo.episodeTitle,
      id: podcastInfo.episodeId,
      podcastId: podcastInfo.podcastId,
      currentTime: audioRef.current?.currentTime,
    });

    listenTimeoutRef.current = setTimeout(async () => {
      // Verify we're still playing and have the same episode
      if (!audioRef.current?.paused && podcastInfo.episodeId) {
        console.log("10s timer completed, verifying playback state:", {
          isPlaying: !audioRef.current?.paused,
          currentTime: audioRef.current?.currentTime,
          episodeId: podcastInfo.episodeId,
        });

        try {
          console.log("Recording listen for episode:", {
            title: podcastInfo.episodeTitle,
            id: podcastInfo.episodeId,
            podcastId: podcastInfo.podcastId,
          });

          // Insert into listen_history which will trigger the metrics update
          const { error: insertError } = await supabase
            .from("listen_history")
            .upsert(
              {
                user_id: null, // null for anonymous users
                episode_id: podcastInfo.episodeId,
                podcast_id: podcastInfo.podcastId,
                progress:
                  audioRef.current.currentTime / audioRef.current.duration,
                completed: false,
                updated_at: new Date().toISOString(),
              },
              {
                onConflict: 'user_id,episode_id'
              }
            );

          if (insertError) {
            console.error("Error recording listen:", insertError);
            return;
          }

          console.log(
            "Listen recorded successfully, incrementing local view count",
          );
          setPodcastInfo((prev) => {
            const newCount = prev.views + 1;
            console.log("Updated view count:", {
              old: prev.views,
              new: newCount,
            });
            return {
              ...prev,
              views: newCount,
            };
          });
        } catch (error) {
          console.error("Error recording listen:", error);
        }
      } else {
        console.log("Playback state changed during 10s wait:", {
          isPlaying: !audioRef.current?.paused,
          currentTime: audioRef.current?.currentTime,
          episodeId: podcastInfo.episodeId,
        });
      }
    }, 10000); // 10 second delay
  };

  useEffect(() => {
    if (!audioRef.current) {
      console.log("No audio ref available");
      return;
    }

    if (isPlaying) {
      console.log("Play triggered, current state:", {
        episodeId: podcastInfo.episodeId,
        title: podcastInfo.episodeTitle,
        hasAudioRef: !!audioRef.current,
        currentTime: audioRef.current.currentTime,
        duration: audioRef.current.duration,
      });

      audioRef.current.play().catch((error) => {
        console.error("Playback failed:", error);
        setIsPlaying(false);
      });

      if (podcastInfo.episodeId) {
        console.log("Starting listen timer with episode data:", {
          episodeId: podcastInfo.episodeId,
          podcastId: podcastInfo.podcastId,
        });
        startListenTimer();
      } else {
        console.log("No episode data available yet, waiting for data load");
      }
    } else {
      console.log("Pause triggered, stopping playback and clearing timer", {
        episodeId: podcastInfo.episodeId,
        hasTimer: !!listenTimeoutRef.current,
      });
      audioRef.current.pause();
      if (listenTimeoutRef.current) {
        clearTimeout(listenTimeoutRef.current);
        listenTimeoutRef.current = undefined;
      }
    }
  }, [isPlaying, podcastInfo]);

  const handleTimeUpdate = () => setCurrentTime(audioRef.current!.currentTime);
  const handleEnded = () => {
    setIsPlaying(false);
    if (listenTimeoutRef.current) {
      clearTimeout(listenTimeoutRef.current);
      listenTimeoutRef.current = undefined;
    }
  };
  const handleError = (e: ErrorEvent) => {
    console.error("Audio playback error:", e);
    setError(new Error("Failed to play audio"));
    setIsPlaying(false);
    setLoading(false);
    if (listenTimeoutRef.current) {
      clearTimeout(listenTimeoutRef.current);
      listenTimeoutRef.current = undefined;
    }
  };

  useEffect(() => {
    if (!url) return;

    audioRef.current = new Audio(decodedUrl);
    const audio = audioRef.current;

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("durationchange", handleDurationChange);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("durationchange", handleDurationChange);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.pause();
      if (listenTimeoutRef.current) {
        clearTimeout(listenTimeoutRef.current);
        listenTimeoutRef.current = undefined;
      }
    };
  }, [url, decodedUrl]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value: number[]) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: podcastInfo.title,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  if (!url) return <div>No audio URL provided</div>;
  if (error) return <div>Failed to load audio</div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#8285c7] via-[#a3b8d4] to-[#aad8dd] p-4">
      <div className="w-[400px] max-w-full bg-black/30 backdrop-blur-xl rounded-[32px] shadow-xl overflow-hidden border border-white/20">
        <div className="p-6 space-y-6">
          <div className="flex flex-col items-center text-center">
            <img
              src={
                podcastInfo.imageUrl ||
                "https://dummyimage.com/120/f4f4f4/666666&text=Podcast"
              }
              alt="Podcast Artwork"
              className="w-24 h-24 rounded-2xl mb-4 shadow-lg"
            />
            <h1 className="text-2xl font-semibold text-white drop-shadow-sm mb-2">
              {podcastInfo.title || "Podcast Title"}
            </h1>
            <p className="text-white/90 drop-shadow-sm">
              {podcastInfo.episodeTitle || "Episode Title"}
            </p>
            <div className="flex items-center gap-4 mt-2 text-white/60">
              <div className="flex items-center gap-1">
                <Play className="w-4 h-4" />
                <span className="text-sm">
                  {podcastInfo.views.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="w-4 h-4" />
                <span className="text-sm">
                  {podcastInfo.likes.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingState label="Loading audio..." />
          ) : (
            <div className="space-y-4">
              <div className="px-3">
                <Slider
                  value={[currentTime]}
                  max={duration}
                  step={1}
                  onValueChange={handleSeek}
                  className="w-full [&_[role=slider]]:bg-white [&_[role=slider]]:border-white [&_[role=slider]]:shadow-none [&_[role=slider]]:ring-white [&_[role=slider]]:ring-offset-0 [&_.relative]:bg-white/30 [&_[role=slider]]:hover:border-white/80 [&_[role=slider]]:focus:border-white/80 [&_[role=slider]]:focus:ring-white/80 [&_[data-filled]]:bg-white [&_[data-filled]]:opacity-100"
                />
                <div className="flex justify-between text-sm text-white/60 mt-1">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12 rounded-full bg-black/20 border-white/20 hover:bg-black/30 text-white"
                  onClick={togglePlay}
                >
                  {isPlaying ? (
                    <Pause className="h-6 w-6" />
                  ) : (
                    <Play className="h-6 w-6" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12 rounded-full bg-black/20 border-white/20 hover:bg-black/30 text-white"
                  onClick={handleShare}
                >
                  <Share2 className="h-6 w-6" />
                </Button>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-white/20">
            <Button
              className="w-full bg-black/20 hover:bg-black/30 text-white border-white/20"
              onClick={() => window.open(window.location.origin, "_blank")}
            >
              Try Krak
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedPlayer;
